import React, { useState, useEffect } from 'react';
import { Box, Typography } from "@mui/material";
import { Plus } from "lucide-react";
import { CustomTextField } from "./CustomTextField.tsx";
import { ActionButton } from "./ActionButton.tsx";

interface CollectionCreatorProps {
  isRoot: boolean;
  value: string;
  onChange: (value: string) => void;
  onCreate: () => void;
  disabled?: boolean;
  error?: string;
  maxLength?: number;
  isDarkMode?: boolean;
  showCharacterCount?: boolean;
  placeholder?: string;
}

export const CollectionCreator: React.FC<CollectionCreatorProps> = ({
  isRoot,
  value,
  onChange,
  onCreate,
  disabled = false,
  error,
  maxLength = isRoot ? 25 : 50,
  isDarkMode = true,
  showCharacterCount = true,
  placeholder
}) => {

  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const handleChange = (inputValue: string) => {
    if (inputValue.length <= maxLength) {
      onChange(inputValue);
    }
  };

  const getDefaultPlaceholder = () => {
    if (placeholder) return placeholder;

    return isRoot
      ? textConfig.COLLECTION_CREATOR_UI_NEW_COLLECTION_PLACEHOLDER
      : textConfig.COLLECTION_CREATOR_UI_NEW_FOLDER_PLACEHOLDER;
  };

  const getButtonLabel = () => {
    return isRoot
      ? textConfig.COLLECTION_CREATOR_UI_CREATE_COLLECTION_BUTTON
      : textConfig.COLLECTION_CREATOR_UI_CREATE_FOLDER_BUTTON;
  };

  const getCharacterCountText = () => {
    return textConfig.COLLECTION_CREATOR_UI_CHARACTER_COUNT
      ?.replace('{current}', value.length.toString())
      ?.replace('{max}', maxLength.toString())
      || `${value.length}/${maxLength} caracteres`;
  };

  const isDisabled = disabled || value.trim() === "" || !!error;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        mb={error || showCharacterCount ? 1 : 2}
        sx={{
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <CustomTextField
          placeholder={getDefaultPlaceholder()}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          error={!!error}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderColor: error ? "#FF3D3D" : undefined,
            },
          }}
          inputProps={{
            maxLength: maxLength,
          }}
        />
        <ActionButton
          label={getButtonLabel()}
          onClick={onCreate}
          Icon={<Plus />}
          disabled={isDisabled}
        />
      </Box>

      {error && (
        <Typography color="#FF3D3D" fontSize="12px" mb={1}>
          {error}
        </Typography>
      )}

      {showCharacterCount && (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Typography
            fontSize="12px"
            color={
              value.length >= maxLength
                ? "#FF3D3D"
                : isDarkMode
                ? "#9CA3AF"
                : "#6B7280"
            }
          >
            {getCharacterCountText()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};